import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import type { Remark, RemarkSettings, RemarksState } from '@roll1/shared';
import { DEFAULT_REMARK_SETTINGS, resolveRemarkSettings, visibleRemarks } from '@roll1/shared';
import type { RemarkInput, RemarksView } from '@roll1/shared';
import { useSessionContext } from '../contexts/SessionContext';

/**
 * «Замечания» (group E, P13): the campaign's remarks state of the GM — switches, the model's remarks (prep + game),
 * what was left as is. ONE store for the tab, shared by every hook instance (the Studio rail and panel, the table's
 * hint at an objective, the session summary): seeded from the join snapshot (`serverState.remarks`, GM only) once per
 * join, then kept by the server's frames. Simple remarks are not here — they are computed from the live data
 * (`visibleRemarks` of @roll1/shared) by whoever shows them.
 */

type SessionMp = ReturnType<typeof useSessionContext>['mp'];

/** The session's connection, or nothing outside a session (isolated tests, partial mocks) — the hook calls are the same either way. */
function useMp(): Partial<SessionMp> | undefined {
  try { return useSessionContext().mp as Partial<SessionMp> | undefined; } catch { return undefined; }
}

const EMPTY: RemarksState = { settings: { ...DEFAULT_REMARK_SETTINGS }, model: [], dismissed: [] };

interface Store { state: RemarksState; seededJoin: number | null }
const store: Store = { state: EMPTY, seededJoin: null };
const listeners = new Set<() => void>();

function set(next: RemarksState): void {
  store.state = next;
  for (const l of [...listeners]) l();
}

/** A server frame about remarks, applied to the store (idempotent: the same frame twice changes nothing more). */
export function applyRemarkFrame(msg: { type: string } & Record<string, unknown>): void {
  switch (msg.type) {
    case 'remark:list':
      set({ ...store.state, model: (msg.remarks as Remark[] | undefined) ?? [] });
      break;
    case 'remark:settings-changed':
      set({ ...store.state, settings: resolveRemarkSettings(msg.settings as Partial<RemarkSettings>) });
      break;
    case 'remark:dismissed': {
      const key = String(msg.key);
      const rest = store.state.dismissed.filter(d => d.key !== key);
      set({ ...store.state, dismissed: [...rest, { key, fingerprint: String(msg.fingerprint) }] });
      break;
    }
    case 'remark:closed':
      set({ ...store.state, model: store.state.model.filter(r => r.key !== String(msg.key)) });
      break;
    default:
      break;
  }
}

/** Test seam: forget everything (a new campaign / a new test). */
export function resetRemarksStore(): void {
  store.state = EMPTY;
  store.seededJoin = null;
}

export interface UseRemarksReturn {
  state: RemarksState;
  settings: RemarkSettings;
  setSetting: (key: keyof RemarkSettings, value: boolean) => void;
  dismiss: (key: string) => void;
  /** The newest hint from the game that is not out of date and not dismissed (the table has ONE place for it). */
  tableHint: Remark | undefined;
  /** What the panel shows for this data (simple remarks by the switches, the model's, minus «left as is»). */
  viewOf: (input: RemarkInput) => RemarksView;
}

export function useRemarks(): UseRemarksReturn {
  // Tolerant: mounted also outside a session (isolated tests) and with partial mocks.
  const mp = useMp();
  const state = useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
    () => store.state,
    () => store.state,
  );

  // Seed from the join snapshot once per join of the socket (the frames keep it after).
  const joinSeq = mp?.joinSeq ?? 0;
  const serverState = mp?.serverState;
  const snapshot = serverState?.remarks;
  useEffect(() => {
    if (serverState === null || serverState === undefined) return;
    if (store.seededJoin === joinSeq) return;
    store.seededJoin = joinSeq;
    set(snapshot === undefined ? EMPTY : {
      settings: resolveRemarkSettings(snapshot.settings),
      model: snapshot.model ?? [],
      dismissed: snapshot.dismissed ?? [],
    });
  }, [serverState, joinSeq, snapshot]);

  const onMessage = mp?.onMessage;
  useEffect(() => onMessage?.((msg) => {
    switch (msg.type) {
      case 'remark:list':
      case 'remark:settings-changed':
      case 'remark:dismissed':
      case 'remark:closed':
        applyRemarkFrame(msg as unknown as { type: string } & Record<string, unknown>);
        break;
      default:
        break;
    }
  }), [onMessage]);

  const send = mp?.send;
  const setSetting = useCallback((key: keyof RemarkSettings, value: boolean): void => {
    // Shown at once; the server's echo (remark:settings-changed) is the truth.
    set({ ...store.state, settings: { ...store.state.settings, [key]: value } });
    send?.({ type: 'remark:settings', settings: { [key]: value } });
  }, [send]);
  const dismiss = useCallback((key: string): void => { send?.({ type: 'remark:dismiss', key }); }, [send]);

  const settings = useMemo(() => resolveRemarkSettings(state.settings), [state.settings]);
  const tableHint = useMemo((): Remark | undefined => {
    if (!settings.gameHints) return undefined;
    const gone = new Set(state.dismissed.map(d => d.key));
    const hints = state.model.filter(r => r.kind === 'game' && r.stale !== true && !gone.has(r.key));
    return hints[hints.length - 1];
  }, [state, settings.gameHints]);
  const viewOf = useCallback((input: RemarkInput): RemarksView => visibleRemarks(input, state), [state]);

  return { state, settings, setSetting, dismiss, tableHint, viewOf };
}
